import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer, RefusalCard } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { FineDistributionPanel } from '@/pages/anatel/FineDistributionPanel';
import { OutcomeMixPanel } from '@/pages/anatel/OutcomeMixPanel';
import { Figure, PageHeader, bacenCasesHref, useCodeLabel, useFormats } from '@/pages/bacen/shared';
import { fetchBacenOverview } from '@/services/bacen';
import type {
  AnswerOut,
  BacenBoardLinkCoverage,
  BacenOfficeSplit,
  BacenPenaltySplit,
} from '@/services/types';

/**
 * BACEN's sanctioning ledger, and what the CRSFN does to a first-instance
 * penalty.
 *
 * One row is one accused in one process, and 13,121 of the rows name a
 * natural person BACEN publishes by name and masked CPF; every figure here
 * counts persons and companies together and says so. "Ruled" means the CRSFN
 * decided the row, a fifth of the corpus, so every rate is over ruled rows
 * and the corpus figure sits beside it. The first instance is an office code,
 * never a person, which is the first refusal at the bottom of the page.
 */
export function BacenOverviewPage() {
  const { t } = useTranslation(['bacen', 'common']);
  const { number, currency, percent } = useFormats();
  const label = useCodeLabel();
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['bacen', 'overview'],
    queryFn: fetchBacenOverview,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  const c = data.corpus;
  const stats: { key: string; value: number; href?: string; second: string }[] = [
    {
      key: 'rows',
      value: c.rows,
      href: bacenCasesHref({}),
      second: t('bacen:overview.corpus.processes', {
        n: number.format(c.processes),
      }),
    },
    {
      key: 'ruled',
      value: c.ruled,
      href: '/bacen/appeals',
      second: t('bacen:overview.corpus.linked', {
        n: number.format(c.linked),
        pending: number.format(c.pending),
      }),
    },
    {
      key: 'persons',
      value: c.persons,
      href: bacenCasesHref({ subject_type: 'person' }),
      second: t('bacen:overview.corpus.personRows', {
        n: number.format(c.person_rows),
      }),
    },
    {
      key: 'entities',
      value: c.entities,
      href: '/bacen/entities',
      second: t('bacen:overview.corpus.companyRows', {
        n: number.format(c.company_rows),
      }),
    },
  ];

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        page="bacen:overview"
        exportPath="/api/v1/bacen/export/overview"
        exportLabel={t('bacen:overview.exportOverview')}
      />

      <AsOfStrip asOf={data.as_of} />

      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.key} className="rounded-lg border bg-card p-3" data-tile={stat.key}>
            <dt className="text-xs text-muted-foreground">
              {t(`bacen:overview.corpus.${stat.key}`)}
            </dt>
            <dd className="mt-1 text-xl font-semibold tabular-nums text-foreground">
              {stat.href ? (
                <Link to={stat.href} className="text-primary underline-offset-2 hover:underline">
                  {number.format(stat.value)}
                </Link>
              ) : (
                number.format(stat.value)
              )}
            </dd>
            <dd className="mt-0.5 text-xs text-muted-foreground">{stat.second}</dd>
          </div>
        ))}
      </dl>

      <FieldGuide i18nKey="bacen:overview.guide.corpus" level={2} />

      <section className="rounded-lg border bg-card p-4" data-waterfall>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('bacen:overview.waterfall.title')}
        </h2>
        <Answer answer={data.reduction_waterfall} help={t('bacen:overview.waterfall.help')}>
          {(w) => (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Figure
                  label={t('bacen:overview.waterfall.firstTotal')}
                  value={currency.format(w.first_total)}
                />
                <Figure
                  label={t('bacen:overview.waterfall.boardTotal')}
                  value={currency.format(w.board_total)}
                />
                <Figure
                  label={t('bacen:overview.waterfall.relief')}
                  value={currency.format(w.total_relief)}
                />
                <Figure
                  label={t('bacen:overview.waterfall.medianRetained')}
                  value={w.median_retained === null ? '—' : percent.format(w.median_retained)}
                />
              </div>
              <dl className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-5">
                {(
                  [
                    ['maintained', w.maintained],
                    ['reduced', w.reduced],
                    ['eliminated', w.eliminated],
                    ['typeChanged', w.type_changed],
                    ['increased', w.increased],
                  ] as const
                ).map(([key, value]) => (
                  <div key={key} className="rounded-md border bg-muted/30 p-2" data-walk={key}>
                    <dt className="text-muted-foreground">
                      {t(`bacen:overview.waterfall.${key}`)}
                    </dt>
                    <dd className="font-semibold tabular-nums text-foreground">
                      {number.format(value)}
                    </dd>
                  </div>
                ))}
              </dl>
              <p className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
                {t('bacen:overview.waterfall.bothDenominators', {
                  reduced: number.format(w.reduced),
                  reviewed: number.format(w.reviewed),
                  withBoth: number.format(w.with_both_fines),
                  ofReviewed: percent.format(w.reduced_share_of_reviewed),
                  total: number.format(c.rows),
                  ofAll: percent.format(c.rows ? w.reduced / c.rows : 0),
                })}
              </p>
            </div>
          )}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="bacen:overview.guide.waterfall" />
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-lg border bg-card p-4" data-board-mix>
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('bacen:overview.boardMix.title')}
          </h2>
          <Answer answer={data.board_review_mix} help={t('bacen:overview.boardMix.help')}>
            {(mix) => (
              <div className="space-y-3">
                <ul className="grid grid-cols-2 gap-2 text-sm">
                  {mix.appeal_kinds.map((kind) => (
                    <li
                      key={kind.code}
                      className="rounded-md border bg-muted/30 p-2"
                      data-appeal-kind={kind.code}
                    >
                      <div className="text-xs text-muted-foreground">
                        {label('appealKind', kind.code)}
                      </div>
                      <div className="font-semibold tabular-nums text-foreground">
                        <Link
                          to={bacenCasesHref({ appeal_kind: kind.code })}
                          className="text-primary underline-offset-2 hover:underline"
                        >
                          {number.format(kind.count)}
                        </Link>
                        <span className="ml-1 text-xs font-normal text-muted-foreground">
                          ({percent.format(kind.share)})
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
                <h3 className="text-xs font-medium text-foreground">
                  {t('bacen:overview.boardMix.transitions', {
                    reviewed: number.format(mix.reviewed),
                  })}
                </h3>
                <ul className="space-y-1 text-sm">
                  {mix.transitions.map((row) => (
                    <li
                      key={row.code}
                      className="flex items-center justify-between gap-3"
                      data-transition={row.code}
                    >
                      <span className="text-muted-foreground">
                        {transitionLabel(row.code, label)}
                      </span>
                      <span className="tabular-nums text-foreground">
                        {number.format(row.count)}
                        <span className="ml-1 text-xs text-muted-foreground">
                          ({percent.format(row.share)})
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Answer>
        </section>
        <FineDistributionPanel
          answer={data.penalty_distribution}
          titleKey="bacen:overview.fines.title"
          helpKey="bacen:overview.fines.help"
          countKey="bacen:overview.fines.count"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <PenaltySplitPanel answer={data.penalty_split} />
        <OutcomeMixPanel
          answer={data.status_mix}
          titleKey="bacen:overview.statusMix.title"
          helpKey="bacen:overview.statusMix.help"
          labelPrefix="bacen:codes.statusFamily."
          rowHref={(code) => bacenCasesHref({ status_family: code })}
        />
      </div>

      <OfficeSplitPanel answer={data.office_split} />

      <BoardLinkPanel answer={data.board_link_coverage} />

      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-foreground">
          {t('bacen:overview.refusals.title')}
        </h2>
        <p className="text-xs text-muted-foreground">{t('bacen:overview.refusals.intro')}</p>
        <div className="grid gap-4 lg:grid-cols-3">
          <RefusalCard
            title={t('bacen:overview.refusals.decisionMakers')}
            answer={data.decision_makers}
          />
          <RefusalCard
            title={t('bacen:overview.refusals.typology')}
            answer={data.infraction_typology}
          />
          <RefusalCard
            title={t('bacen:overview.refusals.precedent')}
            answer={data.precedent_graph}
          />
        </div>
      </section>
    </div>
  );
}

/** "FINE -> NO_PENALTY" rendered with both codes translated. */
export function transitionLabel(
  code: string,
  label: (group: string, code: string | null) => string,
): string {
  const [from, to] = code.split(' -> ');
  if (!to) return label('penalty', code);
  return `${label('penalty', from)} → ${label('penalty', to)}`;
}

/** First-instance penalty type by subject type: persons and companies side by side. */
function PenaltySplitPanel({ answer }: { answer: AnswerOut<BacenPenaltySplit> }) {
  const { t } = useTranslation(['bacen']);
  const { number, percent } = useFormats();
  const label = useCodeLabel();
  return (
    <section className="rounded-lg border bg-card p-4" data-penalty-split>
      <h2 className="mb-3 text-sm font-semibold text-foreground">
        {t('bacen:overview.penaltySplit.title')}
      </h2>
      <Answer answer={answer} help={t('bacen:overview.penaltySplit.help')}>
        {(split) => (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">
                    {t('bacen:overview.penaltySplit.penalty')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('bacen:overview.penaltySplit.rows')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('bacen:overview.penaltySplit.persons')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('bacen:overview.penaltySplit.companies')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('bacen:overview.penaltySplit.reviewed')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {split.rows.map((row) => (
                  <tr key={row.code} className="border-t" data-penalty={row.code}>
                    <td className="px-3 py-2 text-foreground">
                      <Link
                        to={bacenCasesHref({ penalty_code: row.code })}
                        className="text-primary underline-offset-2 hover:underline"
                      >
                        {label('penalty', row.code)}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-foreground">
                      {number.format(row.rows)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {number.format(row.persons)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {number.format(row.companies)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {number.format(row.reviewed)}
                      <span className="ml-1 text-xs">({percent.format(row.reviewed_share)})</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Answer>
    </section>
  );
}

/** The unit that decided at first instance: an office code, alphabetical, never a person. */
function OfficeSplitPanel({ answer }: { answer: AnswerOut<BacenOfficeSplit> }) {
  const { t } = useTranslation(['bacen']);
  const { number, percent } = useFormats();
  return (
    <section className="rounded-lg border bg-card p-4" data-office-split>
      <h2 className="mb-3 text-sm font-semibold text-foreground">
        {t('bacen:overview.offices.title')}
      </h2>
      <Answer answer={answer} help={t('bacen:overview.offices.help')}>
        {(split) => (
          <div className="space-y-2">
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('bacen:overview.offices.office')}</th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('bacen:overview.offices.rows')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('bacen:overview.offices.reviewed')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('bacen:overview.offices.years')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {split.rows.map((row) => (
                    <tr key={row.office} className="border-t" data-office={row.office}>
                      <td className="px-3 py-2 font-mono text-xs text-foreground">
                        <Link
                          to={bacenCasesHref({ office: row.office })}
                          className="text-primary underline-offset-2 hover:underline"
                        >
                          {row.office}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {number.format(row.rows)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(row.reviewed)}
                        <span className="ml-1 text-xs">
                          ({percent.format(row.rows ? row.reviewed / row.rows : 0)})
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {row.first_year ?? '—'}–{row.last_year ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {split.unparsed > 0 ? (
              <p className="text-xs text-muted-foreground" data-unparsed>
                {t('bacen:overview.offices.unparsed', {
                  n: number.format(split.unparsed),
                })}
              </p>
            ) : null}
          </div>
        )}
      </Answer>
      <div className="mt-3">
        <FieldGuide i18nKey="bacen:overview.guide.offices" />
      </div>
    </section>
  );
}

/**
 * How many ruled rows carry a reference the CRSFN docket resolves. PARTIAL
 * while any reference matches nothing; measured per request, so it moves with
 * the two loads.
 */
function BoardLinkPanel({ answer }: { answer: AnswerOut<BacenBoardLinkCoverage> }) {
  const { t } = useTranslation(['bacen']);
  const { number, percent } = useFormats();
  return (
    <section className="rounded-lg border bg-card p-4" data-board-links>
      <h2 className="mb-3 text-sm font-semibold text-foreground">
        {t('bacen:overview.boardLinks.title')}
      </h2>
      <Answer answer={answer} help={t('bacen:overview.boardLinks.help')}>
        {(coverage) => (
          <div className="grid grid-cols-3 gap-3">
            <Figure
              label={t('bacen:overview.boardLinks.reviewed')}
              value={number.format(coverage.reviewed)}
            />
            <Figure
              label={t('bacen:overview.boardLinks.linked')}
              value={number.format(coverage.linked)}
              href="/bacen/crsfn/board"
            />
            <Figure
              label={t('bacen:overview.boardLinks.unlinked')}
              value={`${number.format(coverage.unlinked)} (${percent.format(
                coverage.reviewed ? coverage.unlinked / coverage.reviewed : 0,
              )})`}
            />
          </div>
        )}
      </Answer>
    </section>
  );
}
